import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { apiFetch } from '../bizsuite-app';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import OrgDragDropAssign from '../components/OrgDragDropAssign';

/* ───────── helpers ───────── */
const wfApi = async (path, opts = {}) => {
  try {
    const res = await apiFetch(`/api/workflow${path}`, opts);
    if (!res.ok) return {};
    return res.json();
  } catch { return {}; }
};

/* ───────── Status helpers ─────────
 *  Statuses we care about on the Org Tree (case-insensitive):
 *    Active     → cyan default neon palette
 *    Offered    → orange  (candidate has been issued an offer)
 *    Joining    → green   (offer accepted, awaiting Day-1)
 *    Resigned   → red     (still on the tree but exiting)
 *    Inactive   → HIDDEN — these don't render at all
 *  Anything else falls back to the cyan default. The `isResigned` helper is
 *  kept for back-compat (existing greyed-out pill) but new code should use
 *  `statusKey()` + `STATUS_PALETTE` so the colours stay in one place. */
const statusKey = (status) => {
  if (!status) return 'active';
  return String(status).trim().toLowerCase();
};
const STATUS_PALETTE = {
  active:    { border: '#00E0FF', glow: 'rgba(0,224,255,0.20)', name: '#E5E7EB', chip: 'rgba(0,224,255,0.15)', chipText: '#6FE6FF', bg: 'rgba(0,224,255,0.08)' },
  offered:   { border: '#F59E0B', glow: 'rgba(245,158,11,0.30)', name: '#FBBF24', chip: 'rgba(245,158,11,0.18)', chipText: '#FBBF24', bg: 'rgba(245,158,11,0.08)' },
  joining:   { border: '#22C55E', glow: 'rgba(34,197,94,0.30)',  name: '#86EFAC', chip: 'rgba(34,197,94,0.18)',  chipText: '#86EFAC', bg: 'rgba(34,197,94,0.08)' },
  resigned:  { border: '#FB7185', glow: 'rgba(251,113,133,0.30)', name: '#FCA5A5', chip: 'rgba(251,113,133,0.18)', chipText: '#FCA5A5', bg: 'rgba(251,113,133,0.08)' },
};
const paletteFor = (status) => STATUS_PALETTE[statusKey(status)] || STATUS_PALETTE.active;
const isHiddenStatus = (status) => statusKey(status) === 'inactive';
// Legacy greyed-pill helper — only true for terminal exit-style statuses that
// aren't in our colour palette (e.g. 'Exited', 'Terminated'). 'Resigned' has
// its own red palette and should NOT be treated as a generic resigned/grey pill.
const isResigned = (status) => {
  const s = statusKey(status);
  return s === 'exited' || s === 'terminated';
};

const isPosition = (node) => node.isPosition || node.status === 'position';

/* Format a YYYY-MM-DD or ISO date as "26 Apr 2026" for compact display in the
 * pill sub-line. Returns '' for falsy/garbage input so the caller can skip the
 * row when nothing is set. */
function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return String(d);
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* ───────── Org Tree Node ───────── */
/** Recursive descendant counter — total people reporting up the tree to this
 *  node (direct + indirect). We use it to derive `indirectReports` =
 *  totalDescendants - directReports for the badge under each node. */
function totalDescendants(node) {
  if (!node.children || !node.children.length) return 0;
  let total = node.children.length;
  for (const c of node.children) total += totalDescendants(c);
  return total;
}

/** Inject the connector + pill CSS once. Keyed by id so React's reconciler
 *  doesn't duplicate it across re-renders. Pseudo-elements aren't reachable
 *  via inline styles, so a class-based approach is required for the L-lines. */
function OrgTreeStyles() {
  return (
    <style>{`
      .org-pill-wrap { position: relative; padding-left: 22px; }
      /* Trunk (vertical line) — drawn from top of the wrapper down to the
         pill's vertical centre. Skipped at depth 0 (root has nothing above). */
      .org-pill-wrap.has-trunk::before {
        content: '';
        position: absolute;
        left: 6px; top: 0; height: 50%;
        width: 1px;
        background: linear-gradient(to bottom, transparent, #00E0FF 30%, #00E0FF);
      }
      /* Hook (horizontal line) — from trunk to pill. */
      .org-pill-wrap.has-trunk::after {
        content: '';
        position: absolute;
        left: 6px; top: 50%;
        width: 16px; height: 1px;
        background: #00E0FF;
        box-shadow: 0 0 6px rgba(0,224,255,0.5);
      }
      /* When a node has siblings BELOW it (same parent), the trunk needs to
         continue past this node to the next sibling. We extend the trunk to
         the bottom of the wrapper for non-last siblings via the modifier. */
      .org-pill-wrap.trunk-extend::before {
        height: 100%;
      }
      /* Pill itself */
      .org-pill {
        display: flex; align-items: center; gap: 10px;
        padding: 6px 14px 6px 6px;
        border-radius: 999px;
        border: 1px solid #00E0FF;
        background: rgba(0,224,255,0.08);
        box-shadow: 0 0 10px rgba(0,224,255,0.20);
        cursor: pointer;
        transition: background .15s, box-shadow .15s, transform .1s;
      }
      .org-pill:hover { box-shadow: 0 0 18px rgba(0,224,255,0.45); }
      .org-pill.is-position { border-color: #FF1FA0; background: rgba(255,31,160,0.08); box-shadow: 0 0 10px rgba(255,31,160,0.25); }
      .org-pill.is-resigned { border-color: rgba(148,163,184,0.40); background: rgba(255,255,255,0.02); box-shadow: none; opacity: 0.55; }
    `}</style>
  );
}

function TreeNode({ node, depth = 0, expandedMap, toggleExpand, isLastChild = true }) {
  // Inactive employees are hidden entirely from the Org Tree — see status
  // helpers above. We bail out before rendering AND before mapping children
  // so an inactive sub-tree disappears completely.
  if (isHiddenStatus(node.status)) return null;

  // Children are also filtered for Inactive — they shouldn't even count
  // toward direct/indirect-reports tallies. We compute a filtered list once
  // and use it everywhere below.
  const visibleChildren = (node.children || []).filter(c => !isHiddenStatus(c.status));
  const hasChildren = visibleChildren.length > 0;
  const expanded = expandedMap[node.id] !== false;
  const resigned = isResigned(node.status);
  const position = isPosition(node);
  const directs = visibleChildren.length;
  // Indirect reports = full subtree headcount minus direct children (= the
  // people reporting up to this node TWO or more levels down). Recurse on the
  // filtered children so Inactive descendants don't pad the tally.
  const indirects = hasChildren
    ? visibleChildren.reduce((sum, c) => sum + totalDescendants({ ...c, children: (c.children || []).filter(g => !isHiddenStatus(g.status)) }), 0)
    : 0;

  // Status palette — drives border / name colour / chip styling. Position
  // pills keep their own pink palette (it signals an OPEN slot rather than
  // a person), and 'Exited'/'Terminated' fall back to the legacy greyed pill.
  const palette = paletteFor(node.status);
  const sKey = statusKey(node.status);
  const showStatusChip = sKey === 'offered' || sKey === 'joining' || sKey === 'resigned';

  let pillClass = 'org-pill';
  let nameColor = palette.name;
  if (position) { pillClass += ' is-position'; nameColor = '#FF7AC9'; }
  else if (resigned) { pillClass += ' is-resigned'; nameColor = '#94A3B8'; }
  // Root gets a slightly thicker glow — matched in CSS via the inline override below
  const rootGlow = depth === 0 ? { borderWidth: 2, boxShadow: `0 0 16px ${palette.glow.replace('0.20', '0.55').replace('0.30', '0.55')}` } : null;
  // Apply status palette inline (overrides the default cyan from .org-pill).
  // Skip when this is a position card or a legacy greyed-out exit card.
  const statusInline = (!position && !resigned) ? {
    borderColor: palette.border,
    background: palette.bg,
    boxShadow: `0 0 10px ${palette.glow}`,
  } : null;

  // Wrapper class — drives the connector lines via CSS pseudo-elements
  const wrapClass = [
    'org-pill-wrap',
    depth > 0 ? 'has-trunk' : '',
    !isLastChild ? 'trunk-extend' : '',
  ].filter(Boolean).join(' ');

  // Status-driven sub-line — only rendered for non-Active states. We pull the
  // dates straight off the node (org-structure backend serves these via the
  // SELECT in plugins/workflow-engine/routes/org-structure.js).
  const statusLine = (() => {
    if (sKey === 'offered') {
      const parts = [];
      if (node.date_of_offer) parts.push(`Offered ${fmtDate(node.date_of_offer)}`);
      if (node.expected_joining_date) parts.push(`Expected ${fmtDate(node.expected_joining_date)}`);
      return parts.join(' · ');
    }
    if (sKey === 'joining') {
      if (node.confirmed_joining_date) return `Joining ${fmtDate(node.confirmed_joining_date)}`;
      return '';
    }
    if (sKey === 'resigned') {
      if (node.exit_date) return `Exit ${fmtDate(node.exit_date)}`;
      return '';
    }
    return '';
  })();

  return (
    <div className={wrapClass} style={{ marginLeft: depth === 0 ? 0 : 6, marginBottom: 6 }}>
      <div className={pillClass} style={{ ...(statusInline || {}), ...(rootGlow || {}) }}
        onClick={() => hasChildren && toggleExpand(node.id)}>
        {hasChildren ? (
          <span style={{ fontSize: 11, color: position ? '#FF7AC9' : palette.chipText, width: 14, textAlign: 'center', flexShrink: 0 }}>
            {expanded ? '▾' : '▸'}
          </span>
        ) : <span style={{ width: 14, flexShrink: 0 }} />}
        {!position && (
          <div style={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
            background: node.photo_path ? 'none' : 'linear-gradient(135deg, #3B82F6, #6366F1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 600, color: '#fff', overflow: 'hidden',
            border: node.photo_path ? '1.5px solid rgba(0,224,255,0.40)' : 'none',
          }}>
            {node.photo_path
              ? <img src={node.photo_path} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : ((node.name || '?')[0] || '?')}
          </div>
        )}
        {position && (
          <div style={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
            background: 'rgba(255,31,160,0.15)', border: '1px solid #FF1FA0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, color: '#FF7AC9',
          }}>📋</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 500, color: nameColor, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {node.name || 'Unknown'}
            {node.designation && <span style={{ marginLeft: 8, fontSize: 11.5, color: '#94A3B8', fontWeight: 400 }}>· {node.designation}</span>}
            {node.emp_code && <span style={{ marginLeft: 6, fontSize: 11, color: '#64748B' }}>({node.emp_code})</span>}
            {position && <span style={{ marginLeft: 8, fontSize: 10, background: 'rgba(255,31,160,0.15)', color: '#FF7AC9', padding: '1px 7px', borderRadius: 999, fontWeight: 600 }}>OPEN</span>}
            {resigned && <span style={{ marginLeft: 8, fontSize: 10, background: 'rgba(148,163,184,0.15)', color: '#94A3B8', padding: '1px 7px', borderRadius: 999, fontWeight: 600 }}>{node.status}</span>}
            {/* Status chip — Offered/Joining/Resigned. Active doesn't get
                a chip (the pill itself is the indicator of "everything OK"). */}
            {showStatusChip && !position && (
              <span style={{ marginLeft: 8, fontSize: 10, background: palette.chip, color: palette.chipText, padding: '1px 7px', borderRadius: 999, fontWeight: 600 }}>
                {String(node.status).toUpperCase()}
              </span>
            )}
          </div>
          {/* Status-specific date sub-line. Sits ABOVE the directs/indirects
              line so the most-relevant info for non-Active states is right
              under the name. */}
          {statusLine && (
            <div style={{ fontSize: 10.5, color: palette.chipText, marginTop: 2, fontWeight: 500 }}>
              {statusLine}
            </div>
          )}
          {/* Direct + indirect reports — stacked below the name. Only render
              when there are reports so leaf nodes stay compact. */}
          {(directs > 0 || indirects > 0) && (
            <div style={{ fontSize: 10.5, color: '#94A3B8', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span><strong style={{ color: '#6FE6FF' }}>{directs}</strong> direct</span>
              {indirects > 0 && (
                <span><strong style={{ color: '#A78BFA' }}>{indirects}</strong> indirect</span>
              )}
            </div>
          )}
        </div>
      </div>
      {expanded && hasChildren && visibleChildren.map((c, i) => (
        <TreeNode key={c.id} node={c} depth={depth + 1}
          isLastChild={i === visibleChildren.length - 1}
          expandedMap={expandedMap} toggleExpand={toggleExpand} />
      ))}
    </div>
  );
}

/* ================================================================
   Organogram — visual drag-and-drop org chart
   ================================================================ */

/* Card dimensions */
const CARD_W = 200, CARD_H = 80, GAP_X = 30, GAP_Y = 60;

function layoutTree(roots) {
  const positions = {};
  let nextX = 0;

  function measure(node) {
    const children = (node.children || []);
    if (children.length === 0) {
      const x = nextX;
      nextX += CARD_W + GAP_X;
      return { node, x, w: CARD_W, children: [] };
    }
    const laid = children.map(c => measure(c));
    const totalW = laid.reduce((s, c) => s + c.w, 0) + (laid.length - 1) * GAP_X;
    const startX = laid[0].x;
    const endX = laid[laid.length - 1].x + laid[laid.length - 1].w;
    const midX = (startX + endX) / 2 - CARD_W / 2;
    return { node, x: midX, w: Math.max(totalW, CARD_W), children: laid };
  }

  function assignPositions(laid, depth) {
    const y = depth * (CARD_H + GAP_Y);
    positions[laid.node.id] = { x: laid.x, y, node: laid.node };
    laid.children.forEach(c => assignPositions(c, depth + 1));
  }

  roots.forEach(root => {
    const laid = measure(root);
    assignPositions(laid, 0);
  });
  return positions;
}

function Organogram({ tree, flatData, employees, onReassign, onEditNode }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dragState, setDragState] = useState(null);
  const [pan, setPan] = useState({ x: 40, y: 40 });
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [dropType, setDropType] = useState('direct'); // 'direct' | 'dotted' | 'project'

  const positions = useMemo(() => layoutTree(tree), [tree]);

  // Compute SVG bounds
  const allPos = Object.values(positions);
  const maxX = allPos.reduce((m, p) => Math.max(m, p.x + CARD_W), 0) + 80;
  const maxY = allPos.reduce((m, p) => Math.max(m, p.y + CARD_H), 0) + 80;

  // Build edge list (direct + dotted + project lines)
  const edges = [];
  const dottedEdges = [];
  const projectEdges = [];
  Object.values(positions).forEach(p => {
    const n = p.node;
    if (n.manager_id && positions[n.manager_id]) {
      edges.push({ from: n.manager_id, to: n.id });
    }
    if (n.dotted_manager_id && positions[n.dotted_manager_id]) {
      dottedEdges.push({ from: n.dotted_manager_id, to: n.id });
    }
    if (n.project_manager_id && positions[n.project_manager_id]) {
      projectEdges.push({ from: n.project_manager_id, to: n.id });
    }
  });

  function handleMouseDown(e, nodeId) {
    if (e.button !== 0) return;
    e.stopPropagation();
    const pos = positions[nodeId];
    if (!pos || isPosition(pos.node)) return;
    setDragState({ nodeId, startX: e.clientX, startY: e.clientY, dragging: false });
  }

  function handleMouseMove(e) {
    if (isPanning && panStart.current) {
      setPan(prev => ({
        x: prev.x + (e.clientX - panStart.current.x),
        y: prev.y + (e.clientY - panStart.current.y),
      }));
      panStart.current = { x: e.clientX, y: e.clientY };
      return;
    }
    if (!dragState) return;
    const dx = e.clientX - dragState.startX;
    const dy = e.clientY - dragState.startY;
    if (!dragState.dragging && Math.abs(dx) + Math.abs(dy) > 8) {
      setDragState(prev => ({ ...prev, dragging: true }));
    }
    if (dragState.dragging) {
      // Find drop target
      const rect = containerRef.current.getBoundingClientRect();
      const mx = (e.clientX - rect.left - pan.x) / zoom;
      const my = (e.clientY - rect.top - pan.y) / zoom;
      let found = null;
      Object.entries(positions).forEach(([id, p]) => {
        if (id === dragState.nodeId) return;
        if (mx >= p.x && mx <= p.x + CARD_W && my >= p.y && my <= p.y + CARD_H) {
          found = id;
        }
      });
      setDropTarget(found);
    }
  }

  function handleMouseUp(e) {
    if (isPanning) { setIsPanning(false); panStart.current = null; return; }
    if (dragState && dragState.dragging && dropTarget) {
      onReassign(dragState.nodeId, dropTarget, dropType);
    }
    setDragState(null);
    setDropTarget(null);
  }

  function handleContainerMouseDown(e) {
    if (e.button === 0 && !dragState) {
      setIsPanning(true);
      panStart.current = { x: e.clientX, y: e.clientY };
    }
  }

  function handleWheel(e) {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    setZoom(prev => Math.max(0.2, Math.min(2, prev + delta)));
  }

  // Connector path helper
  function connectorPath(fromId, toId) {
    const f = positions[fromId];
    const t = positions[toId];
    if (!f || !t) return '';
    const x1 = f.x + CARD_W / 2, y1 = f.y + CARD_H;
    const x2 = t.x + CARD_W / 2, y2 = t.y;
    const midY = (y1 + y2) / 2;
    return `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: '#888', fontSize: 13 }}>Drag employee onto another to reassign. Line type:</span>
        {[['direct', 'Direct', '#00e0ff'], ['dotted', 'Dotted Line', '#BF5FFF'], ['project', 'Project Mgr', '#3B8FF4']].map(([key, label, col]) => (
          <button key={key} onClick={() => setDropType(key)} style={{
            padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
            border: `1px solid ${dropType === key ? col : 'rgba(255,255,255,0.15)'}`,
            background: dropType === key ? col + '22' : 'transparent',
            color: dropType === key ? col : '#888',
          }}>{label}</button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#666' }}>
          Zoom: {Math.round(zoom * 100)}% | Scroll to zoom, drag background to pan
        </span>
        <button className="btn-neon btn-sm" onClick={() => { setPan({ x: 40, y: 40 }); setZoom(1); }}>Reset View</button>
      </div>
      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 10, fontSize: 12 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 20, height: 2, background: '#00e0ff', display: 'inline-block' }} /> Direct</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 20, height: 0, borderTop: '2px dashed #BF5FFF', display: 'inline-block' }} /> Dotted</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 20, height: 0, borderTop: '2px dotted #3B8FF4', display: 'inline-block' }} /> Project</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: 'rgba(255,79,79,0.3)', border: '1px solid #FF4F4F', display: 'inline-block' }} /> Position</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: 'rgba(255,255,255,0.05)', border: '1px solid #666', display: 'inline-block' }} /> Resigned</span>
      </div>
      {/* Canvas */}
      <div ref={containerRef}
        style={{ width: '100%', height: 'calc(100vh - 300px)', minHeight: 400, overflow: 'hidden',
          background: 'rgba(0,0,0,0.2)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)',
          cursor: isPanning ? 'grabbing' : (dragState?.dragging ? 'grabbing' : 'grab'), userSelect: 'none',
        }}
        onMouseDown={handleContainerMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
          <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
            {/* Direct edges — solid cyan */}
            {edges.map((e, i) => (
              <path key={'e' + i} d={connectorPath(e.from, e.to)} stroke="#00e0ff" strokeWidth="1.5" fill="none" opacity="0.6" />
            ))}
            {/* Dotted edges — dashed violet */}
            {dottedEdges.map((e, i) => (
              <path key={'d' + i} d={connectorPath(e.from, e.to)} stroke="#BF5FFF" strokeWidth="1.5" fill="none" opacity="0.6" strokeDasharray="6 4" />
            ))}
            {/* Project edges — dotted blue */}
            {projectEdges.map((e, i) => (
              <path key={'p' + i} d={connectorPath(e.from, e.to)} stroke="#3B8FF4" strokeWidth="1.5" fill="none" opacity="0.6" strokeDasharray="3 3" />
            ))}
          </g>
        </svg>
        <div style={{ transform: `translate(${pan.x}px,${pan.y}px) scale(${zoom})`, transformOrigin: '0 0', position: 'relative' }}>
          {Object.entries(positions).map(([id, p]) => {
            const n = p.node;
            const resigned = isResigned(n.status);
            const position = isPosition(n);
            const isDragging = dragState?.dragging && dragState.nodeId === id;
            const isDropTgt = dropTarget === id && dragState?.dragging;

            let cardBorder = 'rgba(255,255,255,0.1)';
            let cardBg = 'rgba(30,30,40,0.95)';
            if (position) { cardBorder = '#FF4F4F'; cardBg = 'rgba(255,79,79,0.1)'; }
            else if (resigned) { cardBorder = '#555'; cardBg = 'rgba(30,30,40,0.6)'; }
            if (isDropTgt) {
              const dtColors = { direct: '#00e0ff', dotted: '#BF5FFF', project: '#3B8FF4' };
              cardBorder = dtColors[dropType]; cardBg = dtColors[dropType] + '22';
            }

            return (
              <div key={id} style={{
                position: 'absolute', left: p.x, top: p.y, width: CARD_W, height: CARD_H,
                background: cardBg, border: `1.5px solid ${cardBorder}`, borderRadius: 8,
                padding: '8px 10px', cursor: position ? 'default' : 'grab',
                opacity: isDragging ? 0.5 : (resigned ? 0.5 : 1),
                transition: isDragging ? 'none' : 'box-shadow .15s',
                boxShadow: isDropTgt ? `0 0 12px ${cardBorder}44` : 'none',
                zIndex: isDragging ? 100 : 1,
                display: 'flex', gap: 8, alignItems: 'center',
              }}
                onMouseDown={(e) => handleMouseDown(e, id)}
                onDoubleClick={() => !position && onEditNode(id)}
              >
                {!position && (
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: n.photo_path ? 'none' : 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden',
                    border: n.photo_path ? '1.5px solid rgba(255,255,255,0.15)' : 'none',
                  }}>
                    {n.photo_path ? (
                      <img src={n.photo_path} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      ((n.name || '?')[0] || '?')
                    )}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: 600, fontSize: 13,
                    color: position ? '#FF4F4F' : (resigned ? '#777' : '#e0e0e0'),
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {position && '📋 '}{n.name || 'Unknown'}
                  </div>
                  <div style={{ fontSize: 11, color: resigned ? '#555' : '#888', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {n.designation || n.emp_code || ''}
                  </div>
                  <div style={{ fontSize: 10, color: '#666', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {n.department || ''}{n.office ? ` • ${n.office}` : ''}
                  </div>
                </div>
                {position && <div style={{ position: 'absolute', top: 4, right: 6, fontSize: 9, background: 'rgba(255,79,79,0.2)', color: '#FF4F4F', padding: '1px 5px', borderRadius: 4 }}>OPEN</div>}
                {resigned && <div style={{ position: 'absolute', top: 4, right: 6, fontSize: 9, background: 'rgba(255,255,255,0.05)', color: '#888', padding: '1px 5px', borderRadius: 4 }}>{n.status}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ───────── Main Component ───────── */
export default function OrgStructure() {
  const [tab, setTab] = useState('tree');
  const [entries, setEntries] = useState([]);
  const [tree, setTree] = useState([]);
  const [flatData, setFlatData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [groupTags, setGroupTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [expandedMap, setExpandedMap] = useState({});
  const [search, setSearch] = useState('');

  /* ── Mass Assignment state ── */
  const [massModal, setMassModal] = useState(false);
  const [massType, setMassType] = useState('direct'); // 'direct' | 'dotted' | 'project'
  const [massManagerId, setMassManagerId] = useState('');
  const [massSearch, setMassSearch] = useState('');
  const [massSkillGroup, setMassSkillGroup] = useState('');
  const [massSelected, setMassSelected] = useState({}); // { empId: true/false }
  const [massSaving, setMassSaving] = useState(false);
  const [massError, setMassError] = useState('');
  const [skillGroups, setSkillGroups] = useState([]);
  const [skillsetOptions, setSkillsetOptions] = useState([]);
  const [empSkillsMap, setEmpSkillsMap] = useState({}); // { empId: [category1, category2] }

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [orgData, treeData, empRes, tagData] = await Promise.all([
      wfApi('/org-structure'),
      wfApi('/org-structure/tree'),
      apiFetch('/api/employees?limit=500').then(r => r.ok ? r.json() : { employees: [] }),
      wfApi('/group-tags'),
    ]);
    setEntries(orgData.entries || []);
    setTree(treeData.tree || []);
    setFlatData(treeData.flat || []);
    setEmployees(empRes.employees || []);
    setGroupTags(tagData.tags || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { loadMassAssignData(); /* also powers DnD tab filters */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Fetch skill groups + employee skill mappings for mass assign ── */
  async function loadMassAssignData() {
    try {
      const [modRes, skillRes] = await Promise.allSettled([
        apiFetch('/api/metadata/cascade'),
        apiFetch('/api/employees/skills/all'),
      ]);
      if (modRes.status === 'fulfilled' && modRes.value.ok) {
        const d = await modRes.value.json();
        setSkillGroups((d.modules || []).map(m => ({ value: m.name, label: m.name })));
        setSkillsetOptions((d.skillsets || []).map(s => ({ value: s.name, label: s.name })));
      }
      if (skillRes.status === 'fulfilled' && skillRes.value.ok) {
        const d = await skillRes.value.json();
        const map = {};
        (d.skills || []).forEach(s => {
          if (!map[s.employee_id]) map[s.employee_id] = new Set();
          // Use module_name (skill group) from the joined metadata tables, fallback to skill_category
          const groupName = s.module_name || s.skill_category;
          if (groupName) map[s.employee_id].add(groupName);
        });
        // Convert Sets to arrays
        const mapped = {};
        Object.entries(map).forEach(([k, v]) => { mapped[k] = [...v]; });
        setEmpSkillsMap(mapped);
      }
    } catch (e) { /* non-critical */ }
  }

  function openMassAssign() {
    setMassType('direct');
    setMassManagerId('');
    setMassSearch('');
    setMassSkillGroup('');
    setMassSelected({});
    setMassError('');
    setMassSaving(false);
    loadMassAssignData();
    setMassModal(true);
  }

  // Filter employees for mass assignment (exclude selected manager)
  const massFilteredEmployees = useMemo(() => {
    return employees.filter(e => {
      if (String(e.id) === String(massManagerId)) return false;
      // Name search
      if (massSearch) {
        const s = massSearch.toLowerCase();
        const name = `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase();
        if (!name.includes(s) && !(e.emp_code || '').toLowerCase().includes(s)) return false;
      }
      // Skill Group filter
      if (massSkillGroup) {
        const cats = empSkillsMap[e.id] || [];
        if (!cats.some(c => c.toLowerCase() === massSkillGroup.toLowerCase())) return false;
      }
      return true;
    });
  }, [employees, massManagerId, massSearch, massSkillGroup, empSkillsMap]);

  function toggleMassSelect(empId) {
    setMassSelected(prev => ({ ...prev, [empId]: !prev[empId] }));
  }

  function toggleMassSelectAll() {
    const allSelected = massFilteredEmployees.every(e => massSelected[e.id]);
    if (allSelected) {
      // Deselect all visible
      const next = { ...massSelected };
      massFilteredEmployees.forEach(e => { next[e.id] = false; });
      setMassSelected(next);
    } else {
      const next = { ...massSelected };
      massFilteredEmployees.forEach(e => { next[e.id] = true; });
      setMassSelected(next);
    }
  }

  async function saveMassAssignment() {
    const selectedIds = Object.entries(massSelected).filter(([, v]) => v).map(([k]) => k);
    if (!massManagerId || selectedIds.length === 0) {
      setMassError('Select a manager and at least one employee.');
      return;
    }
    setMassSaving(true);
    setMassError('');

    const bulkEntries = selectedIds.map(empId => {
      const emp = employees.find(e => String(e.id) === String(empId));
      const effectiveDate = emp?.date_of_joining || '';
      const entry = { employee_id: empId, effective_date: effectiveDate };
      if (massType === 'direct') entry.manager_id = massManagerId;
      else if (massType === 'dotted') entry.dotted_manager_id = massManagerId;
      else if (massType === 'project') entry.project_manager_id = massManagerId;
      return entry;
    });

    try {
      const res = await apiFetch('/api/workflow/org-structure/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries: bulkEntries, managerType: massType }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setMassError(d.error || `Save failed (${res.status})`);
        setMassSaving(false);
        return;
      }
      const result = await res.json();
      setMassSaving(false);
      setMassModal(false);
      fetchData();
    } catch (err) {
      setMassError(err.message || 'Network error');
      setMassSaving(false);
    }
  }

  function toggleExpand(id) {
    setExpandedMap(prev => ({ ...prev, [id]: prev[id] === false ? true : false }));
  }

  function handleEmployeeChange(n, v) {
    const emp = employees.find(e => String(e.id) === String(v));
    setEditRow(prev => ({
      ...prev,
      [n]: v,
      effective_date: prev.effective_date || (emp?.date_of_joining || ''),
    }));
  }

  function openAssign(entry) {
    setEditRow(entry ? {
      id: entry.id,
      employee_id: entry.employee_id,
      manager_id: entry.manager_id || '',
      dotted_manager_id: entry.dotted_manager_id || '',
      project_manager_id: entry.project_manager_id || '',
      group_tag_id: entry.group_tag_id || '',
      effective_date: entry.effective_date || '',
      notes: entry.notes || '',
    } : {
      employee_id: '', manager_id: '', dotted_manager_id: '', project_manager_id: '', group_tag_id: '', effective_date: '', notes: '',
    });
    setSaveError('');
    setEditModal(true);
  }

  // Open edit from organogram by employee id
  function openAssignByEmpId(empId) {
    const existing = entries.find(e => String(e.employee_id) === String(empId));
    if (existing) {
      openAssign(existing);
    } else {
      openAssign(null);
      // Pre-fill employee
      setTimeout(() => {
        setEditRow(prev => prev ? { ...prev, employee_id: empId } : prev);
      }, 50);
    }
  }

  const [saveError, setSaveError] = useState('');

  async function saveAssignment() {
    if (!editRow.employee_id) return;
    setSaveError('');
    try {
      const res = await apiFetch('/api/workflow/org-structure', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editRow),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setSaveError(data.error || `Save failed (${res.status})`);
        return;
      }
      setEditModal(false);
      fetchData();
    } catch (err) {
      setSaveError(err.message || 'Network error');
    }
  }

  // Drag-and-drop reassign from organogram
  async function handleOrgReassign(employeeId, newManagerId, relType) {
    // Find existing entry for this employee
    const existing = entries.find(e => String(e.employee_id) === String(employeeId));
    const payload = {
      id: existing?.id || undefined,
      employee_id: employeeId,
      manager_id: existing?.manager_id || '',
      dotted_manager_id: existing?.dotted_manager_id || '',
      project_manager_id: existing?.project_manager_id || '',
      group_tag_id: existing?.group_tag_id || '',
      effective_date: existing?.effective_date || '',
      notes: existing?.notes || '',
    };

    if (relType === 'direct') payload.manager_id = newManagerId;
    else if (relType === 'dotted') payload.dotted_manager_id = newManagerId;
    else if (relType === 'project') payload.project_manager_id = newManagerId;

    try {
      const res = await apiFetch('/api/workflow/org-structure', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) fetchData();
    } catch (err) { /* handled */ }
  }

  async function deleteEntry(id) {
    if (!confirm('Remove this assignment?')) return;
    await wfApi(`/org-structure/${id}`, { method: 'DELETE' });
    fetchData();
  }

  const empOptions = employees
    .map(e => ({
      value: e.id, label: `${e.first_name} ${e.last_name}${e.emp_code ? ` (${e.emp_code})` : ''}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const tagOptions = groupTags.map(t => ({ value: t.id, label: t.name }));

  const filteredEntries = entries.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (e.employee_name || '').toLowerCase().includes(s) ||
           (e.manager_name || '').toLowerCase().includes(s) ||
           (e.employee_code || '').toLowerCase().includes(s) ||
           (e.group_tag_name || '').toLowerCase().includes(s) ||
           (e.project_manager_name || '').toLowerCase().includes(s) ||
           (e.office_name || '').toLowerCase().includes(s);
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 16, gap: 8 }}>
        <button className="btn btn-secondary" onClick={openMassAssign} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
          Mass Assign
        </button>
        <button className="btn btn-primary" onClick={() => openAssign(null)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Assign Manager
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        {[['tree', 'Org Tree'], ['organogram', 'Organogram'], ['list', 'List View'], ['dnd', 'Drag & Drop']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '10px 24px', background: 'none', border: 'none',
            color: tab === key ? '#00e0ff' : '#888', cursor: 'pointer', fontSize: 14,
            borderBottom: tab === key ? '2px solid #00e0ff' : '2px solid transparent',
            fontWeight: tab === key ? 600 : 400,
          }}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading...</div>
      ) : tab === 'tree' ? (
        /* ─── Tree View ─── */
        <div>
          {tree.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
              No org structure defined yet. Click "Assign Manager" to start building your organization tree.
            </div>
          ) : (
            <>
              {/* Connector + pill styles — single style block per page mount.
                  Pseudo-elements aren't reachable via inline styles, hence
                  the named CSS classes referenced inside TreeNode. */}
              <OrgTreeStyles />
              {tree.map(root => (
                <TreeNode key={root.id} node={root} expandedMap={expandedMap} toggleExpand={toggleExpand} />
              ))}
            </>
          )}
        </div>
      ) : tab === 'organogram' ? (
        /* ─── Organogram View ─── */
        <div>
          {tree.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
              No org structure defined yet. Click "Assign Manager" to start building your organization chart.
            </div>
          ) : (
            <Organogram
              tree={tree}
              flatData={flatData}
              employees={employees}
              onReassign={handleOrgReassign}
              onEditNode={openAssignByEmpId}
            />
          )}
        </div>
      ) : tab === 'dnd' ? (
        /* ─── Drag & Drop (Variant C) ─── */
        <OrgDragDropAssign
          employees={employees}
          entries={entries}
          skillGroups={skillGroups}
          skillsets={skillsetOptions}
          empSkillsMap={empSkillsMap}
          empSkillsetMap={{}}
          onChange={fetchData}
        />
      ) : (
        /* ─── List View ─── */
        <div>
          <div style={{ marginBottom: 12 }}>
            <input type="text" placeholder="Search employees..." value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)',
                background: 'rgba(255,255,255,0.05)', color: '#e0e0e0', width: 300 }} />
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Code</th>
                <th>Office</th>
                <th>Direct Manager</th>
                <th>Dotted Line</th>
                <th>Project Manager</th>
                <th>Group Tag</th>
                <th>Effective Date</th>
                <th style={{ width: 100 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.length === 0 ? (
                <tr><td colSpan="9" style={{ textAlign: 'center', color: '#888', padding: 24 }}>No assignments found</td></tr>
              ) : filteredEntries.map(e => (
                <tr key={e.id}>
                  <td>{e.employee_name || '—'}</td>
                  <td style={{ color: '#888' }}>{e.employee_code || '—'}</td>
                  <td style={{ color: '#FFB300' }}>{e.office_name || '—'}</td>
                  <td>{e.manager_name || <span style={{ color: '#666' }}>None (Top)</span>}</td>
                  <td style={{ color: '#BF5FFF' }}>{e.dotted_manager_name || '—'}</td>
                  <td style={{ color: '#3B8FF4' }}>{e.project_manager_name || '—'}</td>
                  <td><span style={{ color: '#00e0ff' }}>{e.group_tag_name || '—'}</span></td>
                  <td>{e.effective_date || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-neon btn-sm" onClick={() => openAssign(e)} title="Edit">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      <button className="btn-neon btn-sm" onClick={() => deleteEntry(e.id)} title="Remove" style={{ '--page-neon': '#ff4444' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Assign / Edit Modal */}
      <Modal title={editRow?.id ? 'Edit Assignment' : 'Assign Manager'} isOpen={editModal} onClose={() => setEditModal(false)} size="md">
        {editRow && <>
          {saveError && (
            <div style={{ background: 'rgba(255,68,68,0.15)', color: '#ff4444', padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
              {saveError}
            </div>
          )}
          <FormField label="Employee" name="employee_id" type="employee-search" required
            value={editRow.employee_id}
            onChange={handleEmployeeChange}
            options={empOptions}
            placeholder="Search Employee..." />
          <FormField label="Direct Manager (Reports To)" name="manager_id" type="employee-search"
            value={editRow.manager_id}
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))}
            options={empOptions.filter(o => o.value !== editRow.employee_id)}
            placeholder="Search Manager..." />
          <FormField label="Dotted Line Manager" name="dotted_manager_id" type="employee-search"
            value={editRow.dotted_manager_id}
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))}
            options={empOptions.filter(o => o.value !== editRow.employee_id && o.value !== editRow.manager_id)}
            placeholder="Search Manager..." />
          <FormField label="Project Manager" name="project_manager_id" type="employee-search"
            value={editRow.project_manager_id}
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))}
            options={empOptions.filter(o => o.value !== editRow.employee_id && o.value !== editRow.manager_id && o.value !== editRow.dotted_manager_id)}
            placeholder="Search Manager..." />
          <FormField label="Group Tag" name="group_tag_id" type="select"
            value={editRow.group_tag_id}
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))}
            options={tagOptions}
            placeholder="No Tag" />
          <FormField label="Effective Date" name="effective_date" type="date"
            value={editRow.effective_date}
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))} />
          <FormField label="Notes" name="notes" type="textarea"
            value={editRow.notes} rows={2}
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <button className="btn btn-secondary" onClick={() => setEditModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveAssignment}>Save</button>
          </div>
        </>}
      </Modal>

      {/* ── Mass Assignment Modal ── */}
      <Modal title="Mass Assignment" isOpen={massModal} onClose={() => setMassModal(false)} size="lg">
        <div>
          {massError && (
            <div style={{ background: 'rgba(255,68,68,0.15)', color: '#ff4444', padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
              {massError}
            </div>
          )}

          {/* Step 1: Manager Type */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#aaa', marginBottom: 6, fontWeight: 500 }}>Assignment Type</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['direct', 'Direct Manager', '#00e0ff'], ['dotted', 'Dotted Line Manager', '#BF5FFF'], ['project', 'Project Manager', '#3B8FF4']].map(([key, label, col]) => (
                <button key={key} onClick={() => { setMassType(key); setMassSelected({}); }} style={{
                  padding: '8px 18px', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 500,
                  border: `1.5px solid ${massType === key ? col : 'rgba(255,255,255,0.12)'}`,
                  background: massType === key ? col + '18' : 'rgba(255,255,255,0.03)',
                  color: massType === key ? col : '#888',
                  transition: 'all .15s',
                }}>{label}</button>
              ))}
            </div>
          </div>

          {/* Step 2: Select Manager */}
          <FormField
            label={massType === 'direct' ? 'Direct Manager (Reports To)' : massType === 'dotted' ? 'Dotted Line Manager' : 'Project Manager'}
            name="massManager" type="employee-search" required
            value={massManagerId}
            onChange={(n, v) => { setMassManagerId(v); setMassSelected({}); }}
            options={empOptions}
            placeholder="Search Manager..."
          />

          {massManagerId && (
            <>
              {/* Step 3: Filter employees */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 12, marginTop: 8 }}>
                <div style={{ flex: 1 }}>
                  <input type="text" placeholder="Search by name or employee code..."
                    value={massSearch} onChange={e => setMassSearch(e.target.value)}
                    style={{ width: '100%', padding: '8px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)',
                      background: 'rgba(255,255,255,0.05)', color: '#e0e0e0', fontSize: 13 }} />
                </div>
                <div style={{ width: 220 }}>
                  <select value={massSkillGroup} onChange={e => setMassSkillGroup(e.target.value)}
                    style={{ width: '100%', padding: '8px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)',
                      background: 'rgba(255,255,255,0.05)', color: '#e0e0e0', fontSize: 13 }}>
                    <option value="">All Skill Groups</option>
                    {skillGroups.map(sg => <option key={sg.value} value={sg.value}>{sg.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Step 4: Employee list with checkboxes */}
              <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, background: 'rgba(0,0,0,0.15)' }}>
                {/* Select All header */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
                  borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)',
                  position: 'sticky', top: 0, zIndex: 1,
                }}>
                  <input type="checkbox"
                    checked={massFilteredEmployees.length > 0 && massFilteredEmployees.every(e => massSelected[e.id])}
                    onChange={toggleMassSelectAll}
                    style={{ width: 16, height: 16, accentColor: '#00e0ff' }} />
                  <span style={{ fontSize: 13, color: '#aaa', fontWeight: 500 }}>
                    Select All ({massFilteredEmployees.length} employee{massFilteredEmployees.length !== 1 ? 's' : ''})
                  </span>
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: '#00e0ff' }}>
                    {Object.values(massSelected).filter(Boolean).length} selected
                  </span>
                </div>

                {massFilteredEmployees.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#666', fontSize: 13 }}>
                    No employees match the current filters
                  </div>
                ) : massFilteredEmployees.map(emp => {
                  const empSkills = empSkillsMap[emp.id] || [];
                  const existing = entries.find(e => String(e.employee_id) === String(emp.id));
                  // Show current manager of this type
                  let currentMgr = '';
                  if (massType === 'direct' && existing?.manager_name) currentMgr = existing.manager_name;
                  else if (massType === 'dotted' && existing?.dotted_manager_name) currentMgr = existing.dotted_manager_name;
                  else if (massType === 'project' && existing?.project_manager_name) currentMgr = existing.project_manager_name;

                  return (
                    <div key={emp.id} onClick={() => toggleMassSelect(emp.id)} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
                      borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer',
                      background: massSelected[emp.id] ? 'rgba(0,224,255,0.06)' : 'transparent',
                      transition: 'background .1s',
                    }}>
                      <input type="checkbox" checked={!!massSelected[emp.id]} readOnly
                        style={{ width: 16, height: 16, accentColor: '#00e0ff', pointerEvents: 'none' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: '#e0e0e0', fontWeight: 500 }}>
                          {emp.first_name} {emp.last_name}
                          {emp.emp_code && <span style={{ marginLeft: 6, fontSize: 11, color: '#666' }}>({emp.emp_code})</span>}
                        </div>
                        <div style={{ fontSize: 11, color: '#666', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {emp.designation_name && <span>{emp.designation_name}</span>}
                          {emp.department_name && <span>• {emp.department_name}</span>}
                          {emp.date_of_joining && <span>• Joined: {emp.date_of_joining}</span>}
                        </div>
                      </div>
                      {empSkills.length > 0 && (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 160 }}>
                          {empSkills.slice(0, 2).map(s => (
                            <span key={s} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(0,224,255,0.1)', color: '#00e0ff' }}>{s}</span>
                          ))}
                          {empSkills.length > 2 && <span style={{ fontSize: 10, color: '#666' }}>+{empSkills.length - 2}</span>}
                        </div>
                      )}
                      {currentMgr && (
                        <div style={{ fontSize: 10, color: '#888', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={`Current: ${currentMgr}`}>
                          Current: {currentMgr}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Summary + Save */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                <div style={{ fontSize: 13, color: '#888' }}>
                  {Object.values(massSelected).filter(Boolean).length} employee{Object.values(massSelected).filter(Boolean).length !== 1 ? 's' : ''} will be assigned.
                  Effective dates will be auto-set from each employee's joining date.
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-secondary" onClick={() => setMassModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={saveMassAssignment} disabled={massSaving}>
                    {massSaving ? 'Saving...' : `Assign ${Object.values(massSelected).filter(Boolean).length} Employees`}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
